import { useQueryClient, useMutation } from "@tanstack/react-query";
import { triggerNotification } from "components/TheComponents/Notification";
import { useAppContext } from "contexts";
import { ExceptionHandlingType } from "types/apiServiceTypes";
import { getErrorMessageFromResponse } from "helpers/apiHelpers";
import { LeaderboardPrizeSelectionType } from "interfaces";

export type AdditionalPrizeDto = {
  prizeNumber: number;
  amount: number;
};

export type CreateLeaderboardDto = {
  casinoProvider: string;
  startDate: string | null;
  durationDays: number;
  maxPublicUsersCount: number;
  randomPrizeThreshold: number;
  prize1: number;
  prize2: number;
  prize3: number;
  additionalPrizes?: AdditionalPrizeDto[];
  randomPrizes?: AdditionalPrizeDto[];
  prizeSelectionType: LeaderboardPrizeSelectionType;
  ticketRate: number;
};

export function useCreateLeaderboardMutation({
  onSuccessHandler,
}: {
  onSuccessHandler?: () => void;
}) {
  const { apiService } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ body, otp }: { body: CreateLeaderboardDto; otp: string }) => {
      const leaderboard = await apiService.post("/leaderboard/new", body, {
        init: {
          headers: {
            "x-otp": otp,
          },
        },
        exceptionHandlingType: ExceptionHandlingType.MANUAL,
      });

      return leaderboard;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["/leaderboard/new"],
      });

      onSuccessHandler?.();
      triggerNotification({
        type: "success",
        text: "Leaderboard successfully created",
      });
    },
    onError: (error: any) => {
      const statusCode = error?.response?.status || error?.statusCode || error?.status;

      if (statusCode === 409) {
        triggerNotification({
          type: "error",
          text:
            getErrorMessageFromResponse(error) ||
            "Leaderboard with this provider and start date already exists",
        });
        return;
      }

      triggerNotification({
        type: "error",
        text: getErrorMessageFromResponse(error) || "Can't create a leaderboard. Try again later.",
      });
    },
  });
}
